import React, { DragEvent } from "react";
import ReactModal from "react-modal";

import OverflowMenuItem from "./OverflowMenuItem";

import "./PhotoRoll.css";
import ToolbarMenuItem from "./ToolbarMenuItem";

interface IPhotoDef {
    url: string;
    description: string;
}

interface IPhotoRollProps {
    canDelete?: boolean;
    photos: Array<string | true | IPhotoDef>;

    onRequestDelete?: (index: number) => void;
    onRequestUpload?: (files: FileList) => void;
}

interface IPhotoRollState {
    isViewerOpen: boolean;
    isDragHover: boolean;
    dragCounter: number;

    viewerImageIndex: number;
    viewerImage: string;
    viewerDescription: string;
}

class PhotoRoll extends React.Component<IPhotoRollProps, IPhotoRollState> {
    constructor(props: Readonly<IPhotoRollProps>) {
        super(props);

        const state: IPhotoRollState = {
            isViewerOpen: false,
            isDragHover: false,
            dragCounter: 0,

            viewerImage: "",
            viewerImageIndex: 0,
            viewerDescription: "",
        };

        this.state = state;

        this.openViewer = this.openViewer.bind(this);
        this.closeViewer = this.closeViewer.bind(this);
        this.prevImage = this.prevImage.bind(this);
        this.nextImage = this.nextImage.bind(this);
        this.handleDelete = this.handleDelete.bind(this);

        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragDrop = this.handleDragDrop.bind(this);
    }

    public render() {
        const photos = this.props.photos || [];

        return (
            <div className={"photo-roll" + (this.state.isDragHover ? " photo-drag" : "")} onDragEnter={this.handleDragEnter} onDragLeave={this.handleDragLeave} onDragOver={this.handleDragOver} onDrop={this.handleDragDrop}>
                <div className="photo-roll-inner">
                    {
                        this.state.isViewerOpen
                            ? <ReactModal
                                isOpen={this.state.isViewerOpen} onRequestClose={this.closeViewer}
                                overlayClassName="modal-overlay" className="modal-content modal-viewer-content photo-viewer-modal" portalClassName="modal-viewer-portal"
                                bodyOpenClassName="modal-viewer-open"
                                contentLabel="View Image">
                                <div className="photo-viewer">
                                    {this.state.viewerImageIndex > 0 ? <button type="button" className="button btn-light photo-nav-button photo-nav-left" onClick={this.prevImage}><i className="icon-chevron-left" /></button> : null}
                                    <img src={this.state.viewerImage} alt="Full size" />
                                    {this.state.viewerImageIndex < this.props.photos.length - 1 ? <button type="button" className="button btn-light photo-nav-button photo-nav-right" onClick={this.nextImage}><i className="icon-chevron-right" /></button> : null}
                                </div>
                                <div className="photo-description">
                                    {this.state.viewerDescription}
                                    <OverflowMenuItem dropdownClassName="toolbar-menu-up-left" className="button btn-light small">
                                        <ToolbarMenuItem condition={!!this.props.canDelete}>
                                            <button type="button" onClick={this.handleDelete}><i className="icon-trash" /><span>Delete image</span></button>
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem>
                                            <a href={this.state.viewerImage} download={true}><i className="icon-download" /><span>Download image</span></a>
                                        </ToolbarMenuItem>
                                    </OverflowMenuItem>
                                </div>
                            </ReactModal>
                            : null
                    }
                    {
                        photos.length > 0
                            ? photos.map((a, i) => <div key={a === true ? i : ((typeof a === "string" ? a : a.url) || i)} className={a === true ? "photo-message" : ""} onClick={() => this.openViewer(i)}>
                                {a === true ? <span className="spinner spinner-center lds-dual-ring" /> : <img src={(typeof a === "string" ? a : a.url)} alt="Thumbnail" />}
                            </div>)
                            : <div className="photo-message">No Photos</div>
                    }
                </div>
            </div>
        );
    }

    private openViewer(index: number) {
        const photo = this.props.photos[index];
        if (photo === true) {
            return;
        }

        const description = (typeof photo === "string" ? "" : (photo.description || ""));
        const url = (typeof photo === "string" ? photo : photo.url);

        this.setState({
            isViewerOpen: true,
            viewerImage: url,
            viewerImageIndex: index,
            viewerDescription: description,
        });
    }

    private closeViewer() {
        this.setState({
            isViewerOpen: false
        });
    }

    private prevImage() {
        const photo = this.props.photos[Math.max(this.state.viewerImageIndex - 1, 0)];
        if (photo === true) {
            return;
        }

        const description = (typeof photo === "string" ? "" : (photo.description || ""));
        const url = (typeof photo === "string" ? photo : photo.url);

        this.setState(s => ({
            viewerImageIndex: Math.max(s.viewerImageIndex - 1, 0),
            viewerImage: url,
            viewerDescription: description,
        }));
    }

    private nextImage() {
        const photo = this.props.photos[Math.min(this.state.viewerImageIndex + 1, this.props.photos.length - 1)];
        if (photo === true) {
            return;
        }

        const description = (typeof photo === "string" ? "" : (photo.description || ""));
        const url = (typeof photo === "string" ? photo : photo.url);

        this.setState(s => ({
            viewerImageIndex: Math.min(s.viewerImageIndex + 1, this.props.photos.length - 1),
            viewerImage: url,
            viewerDescription: description,
        }));
    }

    private handleDelete() {
        if (this.props.onRequestDelete) {
            this.props.onRequestDelete(this.state.viewerImageIndex);

            this.closeViewer();
        }
    }

    private handleDragEnter(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";

        this.setState(s => ({
            isDragHover: true,
            dragCounter: s.dragCounter + 1
        }));
    }

    private handleDragOver(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        e.dataTransfer.effectAllowed = "copy";
    }

    private handleDragLeave(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();

        this.setState(s => ({
            isDragHover: s.dragCounter - 1 > 0,
            dragCounter: s.dragCounter - 1
        }));
    }

    private handleDragDrop(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.persist();

        if (this.props.onRequestUpload && e.dataTransfer.files.length > 0) {
            this.props.onRequestUpload(e.dataTransfer.files);
        }

        this.setState({
            isDragHover: false,
            dragCounter: 0
        });
    }
}

export default PhotoRoll;
